import { selectReportCoaDimensionFn } from '@opengov/reporting-transforms/dimensions';
import {
  CalendarUnit,
  DateColumnName,
  ECalendarSemanticTypes,
  IDatasetLensColumn,
  IReportDatasetLens,
  IReportDimension,
  LOGICAL_TYPES,
  OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME,
  calendarUnitToDateColumnName,
  calendarUnitToFiscalYearFiscalPeriodColumnName,
  getDisplayDelegateColumn,
  isAccountNumberIdDimensionColumn,
  parseDisplayDelegateSource
} from '@opengov/reporting-types';
import isNil from 'lodash/isNil';
import { LocaleText } from '../../../../locale';
import { IColumnOption, IColumnOptionSegment, IDimensionColumn } from '../../../../types/ColumnOptionTypes';
import { enumValidFilterColumnTypes } from '../../../../types/MultiDatasetTypes';
import { COMBOS_OF_CALENDAR_UNITS, CalendarUnitCombo } from '../../../../utils/calendar';
import {
  GroupByConfig,
  IColumnEditorSetupFormValues,
  IColumnEditorSetupProps,
  IColumnGroupValue
} from './ColumnEditorTypes';

interface IDimensionColumnOption {
  label: string;
  name: string;
}
interface IDateOptions {
  label: string;
  lensId: string;
  name: string;
  dimensionColumns?: IDimensionColumnOption[];
}

export const columnToSelectOption = (column: IDatasetLensColumn) => ({ label: column.label, name: column.name });

export const columnEditorSelectionsValid = (values: IColumnEditorSetupProps | IColumnGroupValue): boolean => {
  const { datasourceId, amount, groupByConfig } = values;
  return !!datasourceId && !!amount && isGroupByConfigValid(groupByConfig);
};

const isGroupByConfigValid = (groupByConfig: GroupByConfig): boolean => {
  switch (groupByConfig.variant) {
    case 'dimension':
      return !!groupByConfig.groupBy;
    case 'multi-hierarchy':
      return !isNil(groupByConfig.segmentId) && !isNil(groupByConfig.groupId);
    case 'multi-hierarchy-account-number':
      // Account-number columns are UNSCOPED — valid with only a segment scope;
      // no group selection is required to enable apply/save.
      return !isNil(groupByConfig.segmentId);
    default: {
      const _exhaustiveCheck: never = groupByConfig;
      throw new Error(`Encountered unknown groupByConfig variant: ${JSON.stringify(_exhaustiveCheck)}`);
    }
  }
};
export const getYearColumns = (lens: IReportDatasetLens): IDatasetLensColumn[] =>
  lens?.columns?.filter((column) => column.semanticTypes.includes(ECalendarSemanticTypes.FISCAL_YEAR));

/**
 * Builds an IColumnOptionSegment containing only the lens's year-typed columns.
 * Used by the multi-hierarchy column editor and builder, which surface Year as
 * a first-class section. MDS callers continue to receive year columns bundled
 * inside the Date segment from `generateDateOptionsForLens`.
 */
export const getYearOptionsForLens = (lens: IReportDatasetLens): IColumnOptionSegment => {
  const yearColumns = getYearColumns(lens);
  let columnOptions: IColumnOption[] = [];
  if (yearColumns && yearColumns.length > 0) {
    columnOptions = yearColumns.map((column) => ({
      lensId: lens.id,
      label: column.label,
      name: column.name
    }));
  }
  return { label: 'Year', columnOptions };
};

export const getPeriodColumns = (lens?: IReportDatasetLens): IDatasetLensColumn[] =>
  lens?.columns.filter((column) => column.semanticTypes.includes(ECalendarSemanticTypes.FISCAL_PERIOD)) ?? [];

export const getFYFPColumns = (lens?: IReportDatasetLens): IDatasetLensColumn[] =>
  lens?.columns.filter(
    (column) =>
      column.semanticTypes.includes(ECalendarSemanticTypes.FISCAL_YEAR_PERIOD) ||
      // new fyfp datasets have only a logical type
      column.type.includes(LOGICAL_TYPES.FISCAL_YEAR_FISCAL_PERIOD)
  ) ?? [];

export const getDateColumns = (lens?: IReportDatasetLens): IDatasetLensColumn[] =>
  lens?.columns.filter((column) => column.type.includes('/logical/date')) ?? [];

export const getDateDimension = (lens?: IReportDatasetLens): IReportDimension => {
  const dimensions = lens?.dimensions ?? [];
  return dimensions.filter((dimension) => dimension.id.includes('/date/'))[0];
};

export const getFiscalYearFiscalPeriodDimension = (lens: IReportDatasetLens): IReportDimension => {
  // dimension id will contain /common/fiscalyearperiod
  const dimensions = lens?.dimensions as IReportDimension[];
  return dimensions.filter((dimension) => dimension.id.includes('/common/fiscalyearperiod/'))[0];
};

export const generateTextOptionsForLens = (
  lens: IReportDatasetLens,
  lensCoaOptions: IColumnOptionSegment,
  lensDateOptions: IColumnOptionSegment
): IColumnOptionSegment => {
  const displayDelegate = getDisplayDelegateColumn(lens);

  return {
    label: 'Text',
    columnOptions: lens?.columns
      ?.filter((column) => {
        // Special-case the account_number_id column. All consistent CoA options have `account_number_id` as the
        // column name and it would get filtered out in the next check.  Without it, we wouldn't be able
        // to generate a column option for it.
        if (isAccountNumberIdDimensionColumn(column)) return true;

        if (column.type !== enumValidFilterColumnTypes.Text) return false;
        if (lensCoaOptions.columnOptions.find((option) => option.name === column.name)) return false;
        if (lensDateOptions.columnOptions.find((option) => option.name === column.name)) return false;
        return true;
      })
      .map((column) => {
        // If a display delegate exists, we want to use it instead of join column.
        if (displayDelegate && parseDisplayDelegateSource(displayDelegate) === column.name) {
          return {
            label: displayDelegate.label,
            name: `${column.name}:${displayDelegate.name}`
          };
        }
        return columnToSelectOption(column);
      })
  };
};

type RequireLocaleText = {
  calendarUnits: LocaleText['calendarUnits'];
  reportExplorer: {
    controls: {
      date: string;
    };
  };
};

export const generateDateOptionsForLens = (
  lens: IReportDatasetLens,
  localeText: RequireLocaleText,
  canUseFYFPColumn?: boolean
): IColumnOptionSegment => {
  const yearColumns = getYearColumns(lens);
  const dateColumns = getDateColumns(lens);

  //Support of period column is behind feature flag

  let dateOptions: IDateOptions[] = [];
  if (dateColumns && dateColumns.length > 0) {
    // For each date column, create an array of options with the correct children for that semantic type
    dateOptions = dateColumns.map((column) => {
      const dimensionColumns: IDimensionColumn[] = [];
      const calendarUnits = COMBOS_OF_CALENDAR_UNITS[CalendarUnitCombo.FullDate];

      calendarUnits.forEach((calendarUnit) => {
        // We don't handle fiscal period as it is not an option for full date
        if (calendarUnit === CalendarUnit.FiscalPeriod) return;
        return dimensionColumns.push({
          label: localeText.calendarUnits[calendarUnit],
          name: calendarUnitToDateColumnName[calendarUnit],
          dimensionId: getDateDimension(lens).id
        });
      });

      return {
        lensId: lens.id,
        label: column.label,
        name: column.name,
        dimensionColumns
      };
    });
  }

  // if feature flag is on... check FY+FP column
  if (canUseFYFPColumn) {
    const fiscalYearFiscalPeriodColumns = getFYFPColumns(lens);

    // If we have fiscal year period column... fiscal period is existed
    if (fiscalYearFiscalPeriodColumns && fiscalYearFiscalPeriodColumns.length > 0) {
      // Combined option of FY and FP
      const FYFPOptions = fiscalYearFiscalPeriodColumns.map((column) => {
        const dimensionColumns: IDimensionColumn[] = [];
        const fyfpOptionsCalendarUnits = COMBOS_OF_CALENDAR_UNITS[CalendarUnitCombo.FiscalYearAndPeriod];

        fyfpOptionsCalendarUnits.forEach((calendarUnit) => {
          // We don't handle fiscal period as it is not an option for fiscal period
          if (calendarUnit === CalendarUnit.Day || calendarUnit === CalendarUnit.Week) return;
          return dimensionColumns.push({
            label: localeText.calendarUnits[calendarUnit],
            name: calendarUnitToFiscalYearFiscalPeriodColumnName[calendarUnit],
            dimensionId: getFiscalYearFiscalPeriodDimension(lens).id
          });
        });

        return {
          lensId: lens.id,
          label: 'Fiscal Year and Period',
          name: column.name,
          dimensionColumns
        };
      });
      dateOptions = dateOptions.concat(FYFPOptions);
    }
  }

  if (yearColumns && yearColumns.length > 0) {
    dateOptions = dateOptions.concat(
      yearColumns.map((column) => {
        return {
          lensId: lens.id,
          label: column.label,
          name: column.name
        };
      })
    );
  }

  return { label: localeText.reportExplorer.controls.date, columnOptions: dateOptions };
};

// prepareDimensionColumnPayload was previously inlined in selectColumnEditorProps,
// but we've extracted it to make it available for use with multi-hierarchy wiring and
// components without depending on unrelated compose-app wiring.
type PrepareDimensionColumnPayloadPayload = Omit<IColumnEditorSetupFormValues, 'groupByConfig'> & {
  groupByConfig: Extract<GroupByConfig, { variant: 'dimension' }>;
};

export function prepareDimensionColumnPayload(
  payload: PrepareDimensionColumnPayloadPayload,
  columnGroupLens: IReportDatasetLens,
  canUseFYFPColumn?: boolean
) {
  return {
    ...payload,
    groupByConfig: prepareDimensionGroupByConfig(payload.groupByConfig, columnGroupLens, canUseFYFPColumn)
  };
}

/**
 * Resolves a dimension organize-by choice to the dimension that backs it.
 *
 * Split out of `prepareDimensionColumnPayload` so a column group with several organize-by levels can
 * prepare each level on its own — the payload carries one level list, but the dimension lookup is
 * per level.
 */
export function prepareDimensionGroupByConfig(
  groupByConfig: Extract<GroupByConfig, { variant: 'dimension' }>,
  columnGroupLens: IReportDatasetLens,
  canUseFYFPColumn?: boolean
): Extract<GroupByConfig, { variant: 'dimension' }> {
  const splitGroupBy = groupByConfig.groupBy.split(':');
  let groupByDimensionId;
  const coaDimension = selectReportCoaDimensionFn(columnGroupLens);
  const dateDimension = getDateDimension(columnGroupLens);

  // if there was a split then it's a dimension column
  // and we have to set groupByDimensionId
  if (splitGroupBy.length === 2) {
    const groupByIsDateColumn = Object.values(calendarUnitToDateColumnName).includes(splitGroupBy[1] as DateColumnName);
    groupByDimensionId = groupByIsDateColumn ? dateDimension.id : coaDimension!.id;

    // If the column is selected as __og_fiscal_year_fiscal_period then use dimension id of fyfp
    // If not then use dateDimension, or COA dimension
    if (canUseFYFPColumn && splitGroupBy[0] === OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME) {
      groupByDimensionId = getFiscalYearFiscalPeriodDimension(columnGroupLens).id;
    }
  }

  return {
    variant: 'dimension' as const,
    groupBy: groupByConfig.groupBy,
    groupByDimensionId
  };
}

export function getDefaultDateGroupByConfig(
  dateSegment: IColumnOptionSegment,
  yearOptions: IColumnOptionSegment
): GroupByConfig {
  const firstDateCol = dateSegment.columnOptions[0];
  if (firstDateCol) {
    const fiscalYearDim = firstDateCol.dimensionColumns?.find((col) => col.name === DateColumnName.FiscalYear);
    if (fiscalYearDim) {
      return {
        variant: 'dimension',
        groupBy: `${firstDateCol.name}:${fiscalYearDim.name}`,
        groupByDimensionId: fiscalYearDim.dimensionId
      };
    }
  }

  const firstYearCol = yearOptions.columnOptions[0];
  if (firstYearCol) {
    return { variant: 'dimension', groupBy: firstYearCol.name };
  }

  return { variant: 'dimension', groupBy: '' };
}
